import { DicomBase } from './dicomBase';
import { DicomDose } from './dicomDose';
import { DicomPlan } from './dicomPlan';
import { DicomRigidRegistration } from './dicomRigidRegistration';
import { DicomStructureSet } from './dicomstructureset';
import { DicomImageSlice } from './dicomImageSlice';
import { Modality, isImageModality } from './modality';

// Define the interface for the DICOM series object
export type DicomSeries = {
    readonly modality: Modality;
    readonly seriesInstanceUID: string;
    readonly seriesDescription: string;
    readonly seriesNumber: number;
    readonly seriesDate?: Date;

    // Frame of Reference UID Attribute (0020,0052)
    readonly frameOfReferenceUid: string;
};

export class Series {
    public readonly seriesModule: DicomSeries;

    private _seriesObjects: DicomBase[] = new Array<DicomBase>();

    public get dicomImageSlices(): DicomImageSlice[] {
        return this._seriesObjects as DicomImageSlice[];
    }

    public get dicomDoses(): DicomDose[] {
        return this._seriesObjects as DicomDose[];
    }

    public get dicomStructureSets(): DicomStructureSet[] {
        return this._seriesObjects as DicomStructureSet[];
    }

    public get dicomPlans(): DicomPlan[] {
        return this._seriesObjects as DicomPlan[];
    }

    public get dicomRegistrations(): DicomRigidRegistration[] {
        return this._seriesObjects as DicomRigidRegistration[];
    }

    constructor(series: DicomSeries) {
        this.seriesModule = series;
    }

    public containsInstance(instance: DicomBase): boolean {
        return this._seriesObjects.some(
            (x) => x.sopInstanceUid === instance.sopInstanceUid
        );
    }

    public addSeriesObject(seriesObject: DicomBase) {
        if (
            this._seriesObjects.every(
                (x) => x.sopInstanceUid !== seriesObject.sopInstanceUid
            )
        ) {
            this._seriesObjects.push(seriesObject);
        }
    }

    public get seriesObjects(): readonly DicomBase[] {
        return this._seriesObjects;
    }

    public get seriesInstanceUID(): string {
        return this.seriesModule.seriesInstanceUID;
    }

    public get numberOfObjects() {
        return this._seriesObjects.length;
    }

    public get frameOfReferenceUid(): string {
        return this.seriesModule.frameOfReferenceUid;
    }

    public get is3DImage(): boolean {
        return isImageModality(this.seriesModule.modality);
    }

    public get isDoseSeries(): boolean {
        return this.seriesModule.modality === Modality.RTDOSE;
    }

    public get isStructuresetSeries(): boolean {
        return this.seriesModule.modality === Modality.RTSTRUCT;
    }

    public get isPlanSeries(): boolean {
        return this.seriesModule.modality === Modality.RTPLAN;
    }

    public get isRegistrationSeries(): boolean {
        return this.seriesModule.modality === Modality.REG;
    }

    public removeSeriesObject(sopInstanceUid: string): boolean {
        const index = this._seriesObjects.findIndex(
            (obj) => obj.sopInstanceUid === sopInstanceUid
        );
        if (index > -1) {
            this._seriesObjects.splice(index, 1);
            return true;
        }
        return false;
    }

    public setExported(): void {
        for (const seriesObject of this._seriesObjects) {
            seriesObject.setExported();
        }
    }
}
